/**
 * Phase 51 Plan 51-07 INTAKE-02 — distinct Triage column for the kanban board.
 *
 * Renders as a leading panel beside the regular swimlane when the board
 * has a column with `isTriage === true` (Phase 51 Plan 51-04 contract).
 * Each task card exposes inline triage actions:
 *   1. "Assign tier" (TaskTierPicker) — ONLY on SLA projects (D-02).
 *   2. "Assign owner" (AssigneePicker) — all triage tasks.
 *   3. "Move to Open" button — calls useMoveTaskToOpen -> POST /api/tasks/:taskId/intake/move-to-open.
 *
 * Triage actions are gated by `canManage` (admin / PM / project member).
 * The server enforces RBAC independently — the UI hiding is convenience only
 * (T-51-71 mitigated in Plan 06 service layer).
 *
 * Error surfacing: 422 error codes from the server are mapped to English
 * inline messages above the "Move to Open" button.
 *   task_intake_assignee_required -> "Assign an owner before moving to Open."
 *   task_tier_required            -> "Assign a tier before moving to Open."
 */
import { useState } from 'react';
import type { TaskCard as TaskCardData, BoardSubproject } from '../../services/api/board';
import type { ProjectSlaTier } from '../../services/api/projectSlaTier';
import { TaskTierPicker } from './TaskTierPicker';
import { AssigneePicker } from './AssigneePicker';
import { useMoveTaskToOpen } from '../../hooks/useMoveTaskToOpen';
import { SLA_TASK_TYPE_ID } from '../../constants/typeIds';

export interface TriageColumnProps {
  /** String projectId (from useParams) forwarded to mutation cache key. */
  projectId: string;
  projectIdNum: number;
  /** All tasks in the triage column (status='triage', taskStateId=null). */
  tasks: TaskCardData[];
  /**
   * Whether the current viewer has triage management authority.
   * true for admin / PM / project member with assignment.
   * Server re-checks this independently — the UI hides actions for clarity.
   */
  canManage: boolean;
  /** Parent project's taskTypeId — used to gate the tier picker (SLA only). */
  projectTaskTypeId?: number | null;
  /** Pre-fetched SLA tiers for this project (from useProjectSlaTiers). */
  tiers?: ProjectSlaTier[];
  /**
   * MKS-419 — merged board only: the readable subprojects by id. A triage card
   * of a subproject shows its slug badge and NO triage actions: the tier
   * picker, owner picker and "Move to Open" are bound to the parent project's
   * tiers and authority.
   */
  subprojectsById?: ReadonlyMap<number, BoardSubproject>;
}

/**
 * Inline error code → English message map (D-08 / D-09).
 * Only the two gated-exit 422 codes need human-readable messages here;
 * other errors display the raw server message.
 */
function getMoveToOpenErrorMessage(err: Error | null): string | null {
  if (!err) return null;
  const msg = err.message ?? '';
  if (msg.includes('task_intake_assignee_required') || msg.toLowerCase().includes('assignee required')) {
    return 'Assign an owner before moving to Open.';
  }
  if (msg.includes('task_tier_required') || msg.toLowerCase().includes('tier required')) {
    return 'Assign a tier before moving to Open.';
  }
  return msg || 'Could not move task to Open. Please try again.';
}

interface TriageTaskCardProps {
  task: TaskCardData;
  projectId: string;
  projectIdNum: number;
  canManage: boolean;
  isSlaProject: boolean;
  tiers: ProjectSlaTier[];
  subproject?: BoardSubproject;
}

function TriageTaskCard({
  task,
  projectId,
  projectIdNum,
  canManage,
  isSlaProject,
  tiers,
  subproject,
}: TriageTaskCardProps) {
  const [moveError, setMoveError] = useState<Error | null>(null);
  const moveToOpen = useMoveTaskToOpen();

  function handleMoveToOpen() {
    setMoveError(null);
    moveToOpen.mutate(
      { taskId: task.id, projectId },
      {
        onError: (err) => setMoveError(err),
      },
    );
  }

  const errorMsg = getMoveToOpenErrorMessage(moveError);

  return (
    <div
      className="rounded-lg border border-amber-200 bg-white p-3 shadow-sm space-y-2"
      data-testid={`triage-card-${task.id}`}
    >
      {/* Task identity */}
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="min-w-0 flex-1">
          {subproject && (
            <span
              data-testid="task-card-subproject-badge"
              title={subproject.name ?? subproject.slug}
              aria-label={subproject.name ?? subproject.slug}
              className="mr-1 inline-flex items-center rounded border border-ms-border bg-ms-canvas px-1.5 py-0.5 text-[10px] font-semibold leading-none text-ms-text-secondary"
            >
              {subproject.slug}
            </span>
          )}
          {task.code && (
            <span className="text-[10px] font-mono text-ms-text-muted mr-1">
              {task.code}
            </span>
          )}
          <span className="text-sm font-medium text-ms-text break-words">
            {task.name}
          </span>
        </div>
      </div>

      {/* Triage actions — visible only to internal triagers (canManage).
          MKS-419: never for a subproject's card (parent-bound tiers/authority). */}
      {canManage && !subproject && (
        <div className="flex flex-col gap-2">
          {/* Action row: tier picker (SLA only) + assignee picker */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Assign tier — SLA projects only (D-02 / T-51-70) */}
            {isSlaProject && tiers.length > 0 && task.tier != null && (
              <TaskTierPicker
                taskId={task.id}
                projectId={projectIdNum}
                audience="internal"
                currentTier={task.tier}
                tiers={tiers}
                canManage={canManage}
                onError={(msg) => setMoveError(new Error(msg))}
              />
            )}
            {isSlaProject && tiers.length > 0 && task.tier == null && (
              /* Untiered SLA task — show a stub "Assign tier" pill that opens the picker */
              <TaskTierPickerUnset
                taskId={task.id}
                projectId={projectIdNum}
                tiers={tiers}
                canManage={canManage}
                onError={(msg) => setMoveError(new Error(msg))}
              />
            )}

            {/* Assign owner */}
            <AssigneePicker
              taskId={task.id}
              projectId={projectIdNum}
              currentAssigneeId={task.assigneeId ?? null}
              currentAssigneeName={task.assigneeName ?? null}
              currentAssigneeColorSeq={task.assigneeColorSeq ?? null}
              currentAssigneeAvatarUrl={task.assigneeAvatarUrl ?? null}
            />
          </div>

          {/* Inline error banner */}
          {errorMsg && (
            <p
              className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
              role="alert"
              data-testid={`triage-move-error-${task.id}`}
            >
              {errorMsg}
            </p>
          )}

          {/* Move to Open button */}
          <button
            type="button"
            onClick={handleMoveToOpen}
            disabled={moveToOpen.isPending}
            className="w-full rounded-md border border-ms-border bg-ms-canvas px-3 py-1.5 text-xs font-medium text-ms-text hover:bg-ms-surface-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid={`triage-move-to-open-${task.id}`}
          >
            {moveToOpen.isPending ? 'Moving…' : 'Move to Open'}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Stub tier picker for SLA tasks that don't have a tier yet.
 * Renders a minimal "Assign tier" trigger with the same TaskTierPicker
 * popover but with a placeholder currentTier so the popover can open.
 */
interface TaskTierPickerUnsetProps {
  taskId: number;
  projectId: number;
  tiers: ProjectSlaTier[];
  canManage: boolean;
  onError?: (message: string) => void;
}

function TaskTierPickerUnset({
  taskId,
  projectId,
  tiers,
  canManage,
  onError,
}: TaskTierPickerUnsetProps) {
  // Use the first tier as a placeholder so TaskTierPicker can open the popover.
  const placeholder = tiers[0];
  if (!placeholder) return null;

  return (
    <TaskTierPicker
      taskId={taskId}
      projectId={projectId}
      audience="internal"
      currentTier={{ id: -1, orderIndex: 0, name: 'Assign tier' }}
      tiers={tiers}
      canManage={canManage}
      onError={onError}
    />
  );
}

// ---------------------------------------------------------------------------
// TriageColumn root
// ---------------------------------------------------------------------------

/**
 * Distinct leading Triage column panel.
 *
 * Rendered by KanbanBoardPage when boardData has a column with isTriage=true.
 * Placed to the left of the KanbanSwimlane as a fixed-width leading panel
 * with an amber accent to visually distinguish it from regular columns.
 */
export function TriageColumn({
  projectId,
  projectIdNum,
  tasks,
  canManage,
  projectTaskTypeId,
  tiers = [],
  subprojectsById,
}: TriageColumnProps) {
  const isSlaProject = projectTaskTypeId === SLA_TASK_TYPE_ID;

  return (
    <div
      // MKS-184 — `min-h-0` so that when the board row is sized to the scroll
      // viewport (a project with no milestones) this stretched sibling can be
      // shorter than its cards and hand the overflow to the card list below,
      // instead of pushing the board taller than the viewport. On a board with
      // milestones the row is still content-sized and nothing changes here.
      className="flex-none w-80 min-h-0 flex flex-col gap-2 rounded-xl border-2 border-amber-300 bg-amber-50 p-3"
      data-testid="triage-column"
    >
      {/* Triage column header — pinned to the board scroll viewport
       * (260911-nh1 / MKS-173) the same way KanbanSwimlane's header band is:
       * `sticky top-0` with `z-10` (deliberately below the swimlane band's
       * `z-20`; the two never overlap horizontally, and the lower value
       * keeps the ordering intent readable). `-mx-3 px-3 -mt-3 pt-3` cancels
       * the root's `p-3` the same way the swimlane band cancels the scroll
       * container's `p-4`, so the amber background spans the column's full
       * width when pinned while the unpinned layout is unchanged.
       *
       * Expected and acceptable: because the root is a stretched flex item in
       * a `h-full` row (not a separate scroll container), this header
       * releases when the Triage column's own box ends — it pins for roughly
       * one viewport of scroll on a much-taller-than-viewport board and then
       * travels off with its column. */}
      <div className="sticky top-0 z-10 bg-amber-50 -mx-3 px-3 -mt-3 pt-3 flex items-center justify-between px-1 py-1">
        <div className="flex items-center gap-2">
          <span
            className="inline-block h-3 w-3 rounded-full shrink-0 bg-amber-400"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-amber-900">Triage</span>
        </div>
        <span className="text-xs font-medium text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">
          {tasks.length}
        </span>
      </div>

      {/* Task list */}
      {tasks.length === 0 ? (
        <p className="px-1 py-4 text-center text-xs text-amber-600 italic">
          No tasks awaiting triage.
        </p>
      ) : (
        // MKS-184 — the triage column scrolls its own cards (`flex-1 min-h-0`
        // takes the space the pinned header leaves, `overflow-y-auto` keeps the
        // rest inside). On a milestone board the column is as tall as the board
        // row, so the list never overflows and this is inert.
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-2">
          {tasks.map((task) => (
            <TriageTaskCard
              key={task.id}
              task={task}
              projectId={projectId}
              projectIdNum={projectIdNum}
              canManage={canManage}
              isSlaProject={isSlaProject}
              tiers={tiers}
              subproject={subprojectsById?.get(task.projectId)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
